import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import {
  Calendar as CalendarIcon,
  Clock,
  Plus,
  X,
  Check,
  Repeat,
  Trash2,
  ChevronLeft,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const EnhancedBarberSchedule = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({ blocks: [], appointments: [], recurring_schedules: [], holidays: [] });
  
  // Recurring schedules
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [newRecurring, setNewRecurring] = useState({
    name: '',
    day_of_week: 0,
    time_slots: [],
    active: true
  });
  
  // Bulk operations
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [bulkReason, setBulkReason] = useState('');
  
  // Shop hours
  const [shopHours, setShopHours] = useState([]);
  
  // Single block form
  const [blockForm, setBlockForm] = useState({
    date: '',
    time_slots: [],
    reason: ''
  });

  const [loading, setLoading] = useState(true);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  // 8 AM – 7:30 PM. Covers standard hours (9-5:30) PLUS Members-only priority
  // hours (8:00, 8:30, 18:00, 18:30, 19:00, 19:30) so barbers can manage them.
  const timeSlots = [];
  for (let hour = 8; hour <= 19; hour++) {
    for (let min = 0; min < 60; min += 30) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
    }
  }

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'barber') {
      navigate('/');
      toast.error('Barber access required');
      return;
    }
    fetchData();
  }, [currentUser, navigate, currentMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const monthStr = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}`;
      
      const [calendarRes, recurringRes, hoursRes] = await Promise.all([
        api.get(`/api/barber-schedule/calendar/${currentUser.id}?month=${monthStr}`),
        api.get(`/api/barber-schedule/recurring-schedules/${currentUser.id}`),
        api.get('/api/barber-schedule/shop-hours')
      ]);
      
      setCalendarData(calendarRes.data);
      setRecurringSchedules(recurringRes.data);
      setShopHours(hoursRes.data.hours || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Adjust so Monday is 0

    const days = [];
    
    // Empty cells before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-zinc-900/50" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const isSelected = selectedDates.includes(dateStr);
      const dayBlocks = calendarData.blocks.filter(b => b.date === dateStr);
      const dayAppointments = calendarData.appointments.filter(a => a.date === dateStr);
      const dayHoliday = calendarData.holidays.find(h => h.date === dateStr);
      
      const isFullyBlocked = dayBlocks.some(b => b.type === 'day_off' || b.type === 'holiday');
      
      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(dateStr)}
          className={`h-24 border border-zinc-800 p-2 cursor-pointer transition-colors ${
            isSelected ? 'bg-lime-900 border-lime-500' :
            isFullyBlocked ? 'bg-red-900/30' :
            'bg-zinc-900 hover:bg-zinc-800'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className={`text-sm font-bold ${isFullyBlocked ? 'text-red-400' : 'text-white'}`}>
              {day}
            </span>
            {isSelected && <Check className="w-4 h-4 text-lime-400" />}
          </div>
          
          {dayHoliday && (
            <Badge className="text-xs bg-purple-500 mb-1">{dayHoliday.name}</Badge>
          )}
          
          <div className="space-y-1">
            {dayAppointments.slice(0, 2).map(apt => (
              <div key={apt.id} className="text-xs bg-blue-900/50 px-1 rounded truncate">
                {apt.time} - {apt.customer_name}
              </div>
            ))}
            {dayAppointments.length > 2 && (
              <div className="text-xs text-zinc-500">+{dayAppointments.length - 2} more</div>
            )}
            
            {dayBlocks.length > 0 && !isFullyBlocked && (
              <div className="text-xs text-yellow-400">
                {dayBlocks.reduce((sum, b) => sum + (b.time_slots?.length || 0), 0)} blocked slots
              </div>
            )}
            {isFullyBlocked && (
              <div className="text-xs text-red-400 font-semibold">Fully Blocked</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (bulkMode) {
      setSelectedDates(prev =>
        prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
      );
    } else {
      setBlockForm({ ...blockForm, date });
    }
  };

  const handleBulkBlock = async () => {
    if (selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    try {
      await api.post('/api/barber-schedule/bulk-block', {
        barber_id: currentUser.id,
        dates: selectedDates,
        time_slots: null, // Full day blocks
        reason: bulkReason || 'Bulk block'
      });

      toast.success(`Blocked ${selectedDates.length} dates`);
      setSelectedDates([]);
      setBulkReason('');
      setBulkMode(false);
      fetchData();
    } catch (error) {
      console.error('Error bulk blocking:', error);
      toast.error('Failed to bulk block dates');
    }
  };

  const handleCreateRecurring = async () => {
    if (!newRecurring.name || newRecurring.time_slots.length === 0) {
      toast.error('Please provide name and time slots');
      return;
    }

    try {
      await api.post('/api/barber-schedule/recurring-schedule', {
        barber_id: currentUser.id,
        ...newRecurring
      });

      toast.success('Recurring schedule created!');
      setNewRecurring({ name: '', day_of_week: 0, time_slots: [], active: true });
      setShowRecurringForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating recurring schedule:', error);
      toast.error('Failed to create recurring schedule');
    }
  };

  const handleDeleteRecurring = async (scheduleId) => {
    try {
      await api.delete(`/api/barber-schedule/recurring-schedule/${scheduleId}`);
      toast.success('Recurring schedule deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting recurring schedule:', error);
      toast.error('Failed to delete recurring schedule');
    }
  };

  const toggleTimeSlot = (slot, target) => {
    if (target === 'new_recurring') {
      setNewRecurring(prev => ({
        ...prev,
        time_slots: prev.time_slots.includes(slot)
          ? prev.time_slots.filter(s => s !== slot)
          : [...prev.time_slots, slot]
      }));
    } else if (target === 'block_form') {
      setBlockForm(prev => ({
        ...prev,
        time_slots: prev.time_slots.includes(slot)
          ? prev.time_slots.filter(s => s !== slot)
          : [...prev.time_slots, slot]
      }));
    }
  };

  const handleQuickBlock = async () => {
    if (!blockForm.date) {
      toast.error('Please select a date from calendar');
      return;
    }

    try {
      if (blockForm.time_slots.length === 0) {
        // Full day block
        await api.post('/api/barber-schedule/mark-day-off', {
          barber_id: currentUser.id,
          start_date: blockForm.date,
          end_date: blockForm.date,
          reason: blockForm.reason || 'Day off'
        });
      } else {
        // Time slot block
        await api.post('/api/barber-schedule/block-time', {
          barber_id: currentUser.id,
          date: blockForm.date,
          time_slots: blockForm.time_slots,
          reason: blockForm.reason || 'Unavailable'
        });
      }

      toast.success('Time blocked successfully!');
      setBlockForm({ date: '', time_slots: [], reason: '' });
      fetchData();
    } catch (error) {
      console.error('Error blocking time:', error);
      toast.error('Failed to block time');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-lime-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-20">
      <div className="container mx-auto px-4 max-w-7xl">
        <h1 className="text-4xl font-bold mb-8">
          <CalendarIcon className="inline-block w-10 h-10 mr-3 text-lime-400" />
          Enhanced Schedule Manager
        </h1>

        <Tabs defaultValue="calendar" className="space-y-6">
          <TabsList className="bg-zinc-900">
            <TabsTrigger value="calendar">Calendar View</TabsTrigger>
            <TabsTrigger value="recurring">Recurring Schedules</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Operations</TabsTrigger>
          </TabsList>

          {/* CALENDAR VIEW */}
          <TabsContent value="calendar" className="space-y-6">
            {/* Calendar Controls */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <CardTitle className="text-lime-400">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                  
                  <Button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className="text-center text-sm font-bold text-zinc-400 p-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-2">
                  {renderCalendar()}
                </div>
              </CardContent>
            </Card>

            {/* Quick Block Form */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lime-400">Quick Block</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Selected Date</Label>
                  <Input
                    value={blockForm.date}
                    readOnly
                    placeholder="Click a date on calendar"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                <div>
                  <Label>Block Type</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      onClick={() => setBlockForm({ ...blockForm, time_slots: [] })}
                      variant={blockForm.time_slots.length === 0 ? 'default' : 'outline'}
                      className={blockForm.time_slots.length === 0 ? 'bg-lime-500 text-black' : ''}
                    >
                      Full Day
                    </Button>
                    <Button
                      onClick={() => {/* Open time picker */}}
                      variant={blockForm.time_slots.length > 0 ? 'default' : 'outline'}
                      className={blockForm.time_slots.length > 0 ? 'bg-lime-500 text-black' : ''}
                    >
                      Specific Times ({blockForm.time_slots.length})
                    </Button>
                  </div>
                </div>

                {blockForm.time_slots.length > 0 && (
                  <div>
                    <Label>Select Time Slots</Label>
                    <div className="grid grid-cols-4 gap-2 mt-2 max-h-40 overflow-y-auto p-2 bg-zinc-800 rounded">
                      {timeSlots.map(slot => {
                        const [h] = slot.split(':').map(Number);
                        const isMemberOnly = h === 8 || h >= 18;
                        return (
                          <Button
                            key={slot}
                            onClick={() => toggleTimeSlot(slot, 'block_form')}
                            size="sm"
                            variant={blockForm.time_slots.includes(slot) ? 'default' : 'outline'}
                            className={blockForm.time_slots.includes(slot) ? 'bg-lime-500 text-black' : ''}
                            title={isMemberOnly ? 'Members-only hour (8am / 6-7:30pm)' : undefined}
                          >
                            {slot}{isMemberOnly && <span className="ml-1 text-[10px] opacity-80">👑</span>}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <Label>Reason (optional)</Label>
                  <Input
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                    placeholder="e.g., Doctor appointment"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                <Button
                  onClick={handleQuickBlock}
                  disabled={!blockForm.date}
                  className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Block {blockForm.time_slots.length === 0 ? 'Full Day' : `${blockForm.time_slots.length} Time Slots`}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RECURRING SCHEDULES */}
          <TabsContent value="recurring" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lime-400">Recurring Schedules</CardTitle>
                  <Button
                    onClick={() => setShowRecurringForm(!showRecurringForm)}
                    className="bg-lime-500 text-black"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {showRecurringForm && (
                  <Card className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4 space-y-4">
                      <Input
                        value={newRecurring.name}
                        onChange={(e) => setNewRecurring({ ...newRecurring, name: e.target.value })}
                        placeholder="Template name (e.g., Lunch Break)"
                        className="bg-zinc-900 border-zinc-700 text-white"
                      />

                      <div>
                        <Label>Day of Week</Label>
                        <select
                          value={newRecurring.day_of_week}
                          onChange={(e) => setNewRecurring({ ...newRecurring, day_of_week: parseInt(e.target.value) })}
                          className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-md p-2 mt-1"
                        >
                          {daysOfWeek.map((day, idx) => (
                            <option key={idx} value={idx}>{day}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label>Time Slots</Label>
                        <div className="grid grid-cols-4 gap-2 mt-2 max-h-40 overflow-y-auto p-2 bg-zinc-900 rounded">
                          {timeSlots.map(slot => {
                            const [h] = slot.split(':').map(Number);
                            const isMemberOnly = h === 8 || h >= 18;
                            return (
                              <Button
                                key={slot}
                                onClick={() => toggleTimeSlot(slot, 'new_recurring')}
                                size="sm"
                                variant={newRecurring.time_slots.includes(slot) ? 'default' : 'outline'}
                                className={newRecurring.time_slots.includes(slot) ? 'bg-lime-500 text-black' : ''}
                                title={isMemberOnly ? 'Members-only hour' : undefined}
                              >
                                {slot}{isMemberOnly && <span className="ml-1 text-[10px] opacity-80">👑</span>}
                              </Button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button onClick={handleCreateRecurring} className="flex-1 bg-lime-500 text-black">
                          <Check className="w-4 h-4 mr-2" />
                          Create
                        </Button>
                        <Button onClick={() => setShowRecurringForm(false)} variant="outline">
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {recurringSchedules.map(schedule => (
                  <Card key={schedule.id || schedule.name} className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Repeat className="w-4 h-4 text-purple-400" />
                            <h3 className="font-semibold text-white">{schedule.name}</h3>
                            <Badge className={schedule.active ? 'bg-lime-500 text-black' : 'bg-zinc-600'}>
                              {schedule.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <p className="text-zinc-400 text-sm">
                            Every {daysOfWeek[schedule.day_of_week]} • {schedule.time_slots.length} time slots
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {schedule.time_slots.map(slot => (
                              <Badge key={slot} className="bg-zinc-700 text-white">{slot}</Badge>
                            ))}
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDeleteRecurring(schedule.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-400 text-red-400 hover:bg-red-400 hover:text-black"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {recurringSchedules.length === 0 && !showRecurringForm && (
                  <div className="text-center py-12 text-zinc-500">
                    <Repeat className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No recurring schedules yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BULK OPERATIONS */}
          <TabsContent value="bulk" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lime-400">Bulk Block Dates</CardTitle>
                  <Button
                    onClick={() => {
                      setBulkMode(!bulkMode);
                      setSelectedDates([]);
                    }}
                    variant={bulkMode ? 'default' : 'outline'}
                    className={bulkMode ? 'bg-lime-500 text-black' : ''}
                  >
                    {bulkMode ? 'Exit Bulk Mode' : 'Enter Bulk Mode'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {bulkMode && (
                  <>
                    <div className="bg-blue-900/30 border border-blue-500 rounded p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                        <div className="text-sm">
                          <p className="text-blue-400 font-semibold mb-1">Bulk Mode Active</p>
                          <p className="text-zinc-300">
                            Click multiple dates on the calendar above to select them for bulk blocking.
                            Selected dates: <strong className="text-lime-400">{selectedDates.length}</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedDates.length > 0 && (
                      <div>
                        <Label>Selected Dates</Label>
                        <div className="flex flex-wrap gap-2 mt-2 p-3 bg-zinc-800 rounded">
                          {selectedDates.map(date => (
                            <Badge key={date} className="bg-lime-500 text-black">
                              {date}
                              <X
                                className="w-3 h-3 ml-2 cursor-pointer"
                                onClick={() => setSelectedDates(prev => prev.filter(d => d !== date))}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Reason</Label>
                      <Textarea
                        value={bulkReason}
                        onChange={(e) => setBulkReason(e.target.value)}
                        placeholder="e.g., Vacation, Training, etc."
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={3}
                      />
                    </div>

                    <Button
                      onClick={handleBulkBlock}
                      disabled={selectedDates.length === 0}
                      className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white"
                    >
                      Block {selectedDates.length} Dates
                    </Button>
                  </>
                )}

                {!bulkMode && (
                  <div className="text-center py-12 text-zinc-500">
                    <CalendarIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="mb-4">Click "Enter Bulk Mode" to start selecting multiple dates</p>
                    <p className="text-sm">Useful for blocking vacation periods, training days, etc.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EnhancedBarberSchedule;